/**
 * ``facet_tags`` filters (report-engine ``FilterConfig.facet_tags``): ONE
 * multi_select whose value is a JSON list of ``"Attribute · Value"`` tags,
 * drawn as one picker PER attribute under a titled section of the drawer
 * (Pricing-Brand "Atributos": Definición, Panel, Pantalla… each with its
 * values). The engine combines the tags — AND across attributes, OR within
 * one — so the UI only has to split and rejoin them; this module is that
 * split, pure and shared by the drawer, the chips and the value formatter.
 */

/** Mirror of ``report_engine.models.FACET_TAG_SEPARATOR``. */
export const FACET_TAG_SEPARATOR = " · ";

export interface FacetGroup {
  attribute: string;
  values: string[];
}

/** ``"Panel · QLED"`` → ``{attribute: "Panel", value: "QLED"}``. A tag
 *  without the separator is an attribute of its own, valued by itself. */
export function splitFacetTag(tag: string): { attribute: string; value: string } {
  const at = tag.indexOf(FACET_TAG_SEPARATOR);
  if (at < 0) return { attribute: tag, value: tag };
  return { attribute: tag.slice(0, at), value: tag.slice(at + FACET_TAG_SEPARATOR.length) };
}

export function facetTag(attribute: string, value: string): string {
  return attribute === value ? value : `${attribute}${FACET_TAG_SEPARATOR}${value}`;
}

/** Tags grouped by attribute, in the order they arrive (the engine sends
 *  them sorted by attribute, numbers in numeric order). */
export function groupFacetTags(tags: ReadonlyArray<string>): FacetGroup[] {
  const groups: FacetGroup[] = [];
  const byAttribute = new Map<string, FacetGroup>();
  for (const tag of tags) {
    const { attribute, value } = splitFacetTag(String(tag));
    let group = byAttribute.get(attribute);
    if (!group) {
      group = { attribute, values: [] };
      byAttribute.set(attribute, group);
      groups.push(group);
    }
    group.values.push(value);
  }
  return groups;
}

/** The selected tags of a filter value (a JSON list; anything else = none). */
export function parseFacetSelection(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x) => x != null).map((x) => String(x)) : [];
  } catch {
    return [];
  }
}

/** The new filter value after ONE attribute's picker changed: that
 *  attribute's tags replaced by ``values``, every other attribute kept
 *  as it was. Empty selection = ``""`` (the filter's "All"). */
export function withFacetAttribute(
  raw: string | null | undefined,
  attribute: string,
  values: ReadonlyArray<string>,
): string {
  const kept = parseFacetSelection(raw).filter((tag) => splitFacetTag(tag).attribute !== attribute);
  const next = [...kept, ...values.map((value) => facetTag(attribute, value))];
  return next.length === 0 ? "" : JSON.stringify(next);
}
